import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Bucket, Pick } from '../types'
import { setVoterDisplayName, submitPicks, type WirePick } from '../api/client'

interface SessionState {
  shareToken: string | null
  picks: Record<string, Pick>
  /**
   * Pending picks that haven't been accepted by the server yet. We
   * flush them on every `setBucket`/`setNote`, but if the network is
   * down they wait here until `flushPending` is called or the next
   * mutation triggers a retry.
   */
  pending: string[] // photo ids
  /**
   * Photo ids whose pick was withdrawn but whose withdrawal the server has not
   * accepted yet. Kept separately because a cleared pick is, by definition, no
   * longer in `picks` — and a batch built only from `picks` can't express "this
   * one is gone", so removals used to be dropped on the floor: the client
   * showed the pick removed while the studio kept seeing it as selected.
   */
  cleared: string[]
  /** Optional name the client gives. Persisted to the studio as soon as they
   *  stop typing, not only when they finalize — see setVoterName. */
  voterName: string
  setVoterName: (name: string) => void
  /** Optional address the client gives. The anchor the studio uses to tell one
   *  person's several browsers apart from several people. */
  voterEmail: string
  setVoterEmail: (email: string) => void
  /**
   * Has this reviewer been asked who they are yet?
   *
   * The ask happens at their FIRST verdict, not at the end — the convention in
   * review and proofing tools, where the identity given at the first selection
   * is what the studio's activity list is keyed on. Asking in the Send panel
   * meant anyone who browsed, or who never finished, stayed anonymous forever.
   */
  identityAsked: boolean
  markIdentityAsked: () => void
  /** True the moment a verdict exists but nobody has been asked yet. */
  shouldAskIdentity: () => boolean
  setShareToken: (token: string) => void
  setBucket: (photoId: string, bucket: Bucket) => void
  clearBucket: (photoId: string) => void
  setNote: (photoId: string, note: string) => void
  flushPending: () => Promise<{ accepted: number } | null>
  finalize: () => Promise<{ accepted: number } | null>
  reset: () => void
}

export const useSession = create<SessionState>()(
  persist(
    (set, get) => {
      let scheduled: number | null = null
      let nameTimer: number | null = null
      const schedule = () => {
        if (scheduled !== null) return
        scheduled = window.setTimeout(() => {
          scheduled = null
          void get().flushPending()
        }, 600)
      }
      return {
        shareToken: null,
        picks: {},
        pending: [],
        cleared: [],
        voterName: '',
        setVoterName: (name) => {
          const next = name.slice(0, 80)
          set({ voterName: next })
          // Send it once they pause. The name used to reach the studio only on
          // finalize, so anyone who typed it and then browsed away — or never
          // submitted at all — showed up as "Reviewer 2" forever. Debounced
          // because this fires on every keystroke; best-effort because a name
          // is a courtesy and must never block reviewing.
          if (nameTimer !== null) window.clearTimeout(nameTimer)
          nameTimer = window.setTimeout(() => {
            nameTimer = null
            const { shareToken, voterName, voterEmail } = get()
            if (!shareToken) return
            if (!voterName.trim() && !voterEmail.trim()) return
            void setVoterDisplayName(shareToken, voterName, voterEmail)
          }, 800)
        },
        voterEmail: '',
        setVoterEmail: (email) => {
          const next = email.slice(0, 320)
          set({ voterEmail: next })
          if (nameTimer !== null) window.clearTimeout(nameTimer)
          nameTimer = window.setTimeout(() => {
            nameTimer = null
            const { shareToken, voterName, voterEmail } = get()
            if (!shareToken) return
            if (!voterName.trim() && !voterEmail.trim()) return
            void setVoterDisplayName(shareToken, voterName, voterEmail)
          }, 800)
        },
        identityAsked: false,
        markIdentityAsked: () => set({ identityAsked: true }),
        shouldAskIdentity: () => {
          const { identityAsked, picks, voterName, voterEmail } = get()
          if (identityAsked || voterName.trim() || voterEmail.trim()) return false
          return Object.keys(picks).length > 0
        },
        setShareToken: (token) => {
          if (get().shareToken !== token) {
            // Different link → wipe any local state from a previous
            // session so picks don't leak across photographers.
            set({
              shareToken: token,
              picks: {},
              pending: [],
              cleared: [],
              voterName: '',
              voterEmail: '',
              identityAsked: false,
            })
          }
        },
        setBucket: (photoId, bucket) => {
          set((s) => ({
            picks: {
              ...s.picks,
              [photoId]: {
                ...(s.picks[photoId] ?? { photoId }),
                bucket,
                updatedAt: Date.now(),
              },
            },
            pending: s.pending.includes(photoId) ? s.pending : [...s.pending, photoId],
            cleared: s.cleared.filter((id) => id !== photoId),
          }))
          schedule()
        },
        clearBucket: (photoId) => {
          set((s) => {
            const next = { ...s.picks }
            delete next[photoId]
            return {
              picks: next,
              pending: s.pending.includes(photoId) ? s.pending : [...s.pending, photoId],
              cleared: s.cleared.includes(photoId) ? s.cleared : [...s.cleared, photoId],
            }
          })
          schedule()
        },
        setNote: (photoId, note) => {
          set((s) => {
            const existing = s.picks[photoId]
            if (!existing) return s
            return {
              picks: {
                ...s.picks,
                [photoId]: { ...existing, note: note || undefined, updatedAt: Date.now() },
              },
              pending: s.pending.includes(photoId) ? s.pending : [...s.pending, photoId],
            }
          })
          schedule()
        },
        flushPending: async () => {
          const { shareToken, pending, picks } = get()
          if (!shareToken || pending.length === 0) return null
          // A pending id with no pick behind it is a WITHDRAWAL. Mapping it to
          // `picks[id]` and filtering the undefined away silently dropped every
          // removal: the studio kept the original verdict forever.
          const batch: WirePick[] = pending.map((id) => {
            const pick = picks[id]
            return pick
              ? { photoId: pick.photoId, bucket: pick.bucket, note: pick.note }
              : { photoId: id, bucket: 'clear' as const }
          })
          if (batch.length === 0) {
            set({ pending: [] })
            return { accepted: 0 }
          }
          try {
            const res = await submitPicks(shareToken, batch, false)
            set((s) => ({
              pending: s.pending.filter((id) => !batch.some((b) => b.photoId === id)),
              cleared: s.cleared.filter((id) => !batch.some((b) => b.photoId === id)),
            }))
            return res
          } catch (err) {
            // Leave pending in place; we'll retry on the next mutation.
            console.warn('[session] flush failed', err)
            return null
          }
        },
        finalize: async () => {
          const { shareToken, picks, cleared, voterName } = get()
          if (!shareToken) {
            throw new Error('This share link is no longer active. Please reopen the original link.')
          }
          // Always submit the full pick set on finalize — pending vs. flushed
          // doesn't matter once we're sealing the link; the server is
          // idempotent and we want the photographer to see the final state.
          // Withdrawals ride along explicitly: a pick that was flushed and then
          // removed is absent from `picks`, so only an explicit 'clear' tells
          // the server to drop the row it already has.
          const all: WirePick[] = [
            ...Object.values(picks).map((p) => ({
              photoId: p.photoId,
              bucket: p.bucket,
              note: p.note,
            })),
            ...cleared
              .filter((id) => !picks[id])
              .map((id) => ({ photoId: id, bucket: 'clear' as const })),
          ]
          const res = await submitPicks(shareToken, all, true, { voterName })
          set({ pending: [], cleared: [] })
          return res
        },
        reset: () =>
          set({
            picks: {},
            pending: [],
            cleared: [],
            voterName: '',
            voterEmail: '',
            identityAsked: false,
          }),
      }
    },
    {
      name: 'shotselect-client-picks-v2',
      partialize: (s) => ({
        shareToken: s.shareToken,
        picks: s.picks,
        pending: s.pending,
        cleared: s.cleared,
        voterName: s.voterName,
        voterEmail: s.voterEmail,
        identityAsked: s.identityAsked,
      }),
    }
  )
)

export function selectCounts(picks: Record<string, Pick>) {
  let love = 0,
    maybe = 0,
    skip = 0,
    notes = 0
  for (const p of Object.values(picks)) {
    if (p.bucket === 'love') love++
    else if (p.bucket === 'maybe') maybe++
    else if (p.bucket === 'skip') skip++
    if (p.note && p.note.trim().length > 0) notes++
  }
  return { love, maybe, skip, notes }
}
